import { useMemo } from 'react';
import { formatCompactNumber } from '@/lib/utils/formatNumber';
import { convertDbToDisplay } from '@/utils/currency';
import { BASE_ANALYTICS_STATS } from '../components/common/constants';
import {
  calculateCampaignAggregates,
  calculateTopInfluencers,
  calculateTopPosts,
} from '../services/analyticsCalculations.service';
import type { Stat } from '../components/metrics/StatSection';
import type { TopInfluencer } from '../types/analytics.types';
import type { TopPost } from '@/hooks/useTopPerformingPosts';
import type { DeliverableAggregate } from '@/types/performanceMetrics';
import type { FormattedInfluencer } from '@/hooks/useAllInfluencers';

export interface UseAnalyticsCalculationsProps {
  deliverableAggregates: DeliverableAggregate[];
  budgetData?: {
    totalBudget: number;
    amountSpent: number;
    remainingBudget: number;
    budgetUtilization?: number;
  };
  allPosts: TopPost[];
  allInfluencers: FormattedInfluencer[];
  showAllInfluencers: boolean;
  showAllPosts: boolean;
  userCountry?: string | null;
  symbol: string;
}

export function useAnalyticsCalculations({
  deliverableAggregates,
  budgetData,
  allPosts,
  allInfluencers,
  showAllInfluencers,
  showAllPosts,
  userCountry,
  symbol,
}: UseAnalyticsCalculationsProps) {
  // Aggregate metrics via pure service
  const aggregates = useMemo(
    () => calculateCampaignAggregates(deliverableAggregates, budgetData),
    [deliverableAggregates, budgetData]
  );

  const {
    totalFollowers,
    totalReach,
    totalEngagement,
    totalLikes,
    totalComments,
    totalShares,
    totalInfluencers,
    totalPosts,
    totalViews,
    committed,
    spent,
    remaining,
    cpv,
    cpe,
  } = aggregates;

  // Stats array
  const stats: Stat[] = useMemo(
    () =>
      BASE_ANALYTICS_STATS.map((s) => {
        if (s.label === 'Engagement') return { ...s, value: formatCompactNumber(totalEngagement) };
        if (s.label === 'Influencers') return { ...s, value: totalInfluencers };
        if (s.label === 'Posts') return { ...s, value: totalPosts };
        if (s.label === 'Views') return { ...s, value: formatCompactNumber(totalViews) };
        if (s.label === 'Committed') {
          return {
            ...s,
            value:
              symbol +
              formatCompactNumber(
                Number(convertDbToDisplay(committed, userCountry).toFixed(2)),
                2
              ),
          };
        }
        if (s.label === 'Spent') {
          return {
            ...s,
            value:
              symbol +
              formatCompactNumber(
                Number(convertDbToDisplay(spent, userCountry).toFixed(2)),
                2
              ),
          };
        }
        if (s.label === 'Remaining') {
          return {
            ...s,
            value:
              symbol +
              formatCompactNumber(
                Number(convertDbToDisplay(remaining, userCountry).toFixed(2)),
                2
              ),
          };
        }
        if (s.label === 'Total Likes') return { ...s, value: formatCompactNumber(totalLikes) };
        if (s.label === 'Total Comments') return { ...s, value: formatCompactNumber(totalComments) };
        if (s.label === 'Total Shares') return { ...s, value: formatCompactNumber(totalShares) };
        if (s.label === 'CPV') {
          return { ...s, value: symbol + convertDbToDisplay(cpv, userCountry).toFixed(2) };
        }
        if (s.label === 'CPE') {
          return { ...s, value: symbol + convertDbToDisplay(cpe, userCountry).toFixed(2) };
        }
        return s;
      }),
    [
      totalEngagement,
      totalInfluencers,
      totalPosts,
      totalViews,
      committed,
      spent,
      remaining,
      totalLikes,
      totalComments,
      totalShares,
      cpv,
      cpe,
      symbol,
      userCountry,
    ]
  );

  // Top performing influencers
  const topInfluencers: TopInfluencer[] = useMemo(
    () => calculateTopInfluencers(deliverableAggregates, allInfluencers, showAllInfluencers, userCountry),
    [deliverableAggregates, allInfluencers, showAllInfluencers, userCountry]
  );

  const followerMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const agg of deliverableAggregates) {
      if (agg.influencer_name && agg.influencer_followers) {
        map.set(agg.influencer_name.toLowerCase().trim(), agg.influencer_followers);
      }
    }
    for (const inf of allInfluencers) {
      if (inf.name && inf.followersCount) {
        map.set(inf.name.toLowerCase().trim(), inf.followersCount);
      }
    }
    return map;
  }, [deliverableAggregates, allInfluencers]);

  const enrichedPosts: TopPost[] = useMemo(() => {
    return allPosts.map((post) => {
      let erFollowers = post.erFollowers;
      if (erFollowers === null || erFollowers === undefined) {
        const followers = followerMap.get(post.influencerName?.toLowerCase().trim() || '');
        if (followers && followers > 0) {
          erFollowers = (((post.likes || 0) + (post.comments || 0)) / followers) * 100;
        }
      }

      const erViews =
        post.erViews !== null && post.erViews !== undefined
          ? post.erViews
          : post.views && post.views > 0
            ? (post.engagementRate || (((post.likes || 0) + (post.comments || 0) + (post.shares || 0)) / post.views) * 100)
            : null;

      return {
        ...post,
        erFollowers: erFollowers ?? null,
        erViews: erViews ?? null,
      };
    });
  }, [allPosts, followerMap]);

  // Top performing posts
  const topPosts: TopPost[] = useMemo(
    () => calculateTopPosts(enrichedPosts, showAllPosts),
    [enrichedPosts, showAllPosts]
  );

  // KPI cards
  const kpiCards = useMemo(() => {
    const totalBudget = budgetData?.totalBudget ?? 0;
    const spentAmount = budgetData?.amountSpent ?? 0;
    const budgetDisplay = totalBudget > 0 ? totalBudget : committed;

    const totalViewsFormatted = formatCompactNumber(totalViews);
    const totalReachFormatted = formatCompactNumber(Math.round(totalReach));
    const totalFollowersFormatted = formatCompactNumber(totalFollowers);
    const spentFormatted =
      symbol +
      formatCompactNumber(Number(convertDbToDisplay(spentAmount, userCountry).toFixed(2)), 2);
    const budgetFormatted =
      symbol +
      formatCompactNumber(Number(convertDbToDisplay(budgetDisplay, userCountry).toFixed(2)), 2);

    const erPercentage =
      totalFollowers > 0
        ? `${((totalEngagement / totalFollowers) * 100).toFixed(1)}%`
        : '0.0%';

    return [
      {
        title: 'Total Views',
        value: totalViewsFormatted,
        subtitle: `${totalFollowersFormatted} followers reached`,
        icon: 'Eye',
        badgeColor: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300',
      },
      {
        title: 'Engagement Rate',
        value: erPercentage,
        subtitle: `${formatCompactNumber(totalEngagement)} interactions`,
        icon: 'TrendingUp',
        badgeColor: 'bg-pink-50 text-pink-700 dark:bg-pink-950/50 dark:text-pink-300',
      },
      {
        title: 'Total Reach',
        value: totalReachFormatted,
        subtitle: 'Estimated unique impressions',
        icon: 'Users',
        badgeColor: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
      },
      {
        title: 'Total Spend',
        value: spentFormatted,
        subtitle: `of ${budgetFormatted} budget`,
        icon: 'DollarSign',
        badgeColor: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
      },
    ];
  }, [
    totalViews,
    totalFollowers,
    totalReach,
    totalEngagement,
    budgetData,
    committed,
    userCountry,
    symbol,
  ]);

  return {
    totalViews,
    stats,
    topInfluencers,
    topPosts,
    kpiCards,
  };
}
